import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import api from '../lib/api';

interface DecodedToken {
  id: string;
  email: string;
  role: string;
}

interface MatchLobbyProps {
  match: any;
  onMatchUpdate?: () => void | Promise<void>;
}

const MatchLobby = ({ match, onMatchUpdate }: MatchLobbyProps) => {
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [isInvitedTeam, setIsInvitedTeam] = useState(false);
  const [isHostTeam, setIsHostTeam] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Get current user ID from token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken>(token);
        setCurrentUserId(decoded.id);

        // Check if user is in the invited team (right side) or host team (left side)
        const leftParticipant = match.participants.find((p: any) => p.side === 'left');
        const rightParticipant = match.participants.find((p: any) => p.side === 'right');

        // For team matches, check team membership
        if (match.mode === 'competitive') {
          // Check if user is in the invited (right) team
          if (rightParticipant?.team?.users) {
            const isInvited = rightParticipant.team.users.some((ut: any) => ut.userId === decoded.id);
            setIsInvitedTeam(isInvited);
          }

          // Check if user is in the host (left) team
          if (leftParticipant?.team?.users) {
            const isHost = leftParticipant.team.users.some((ut: any) => ut.userId === decoded.id);
            setIsHostTeam(isHost);
          }
        }
      } catch (error) {
        console.error('Failed to decode token:', error);
      }
    }
  }, [match]);

  const handleAcceptMatch = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/matches/${match.id}/accept`);
      console.log('Match accepted, new status:', response.data.status);

      // Force refresh the match data multiple times to ensure update
      if (onMatchUpdate) {
        await onMatchUpdate();
        // Wait a bit and refresh again to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 500));
        await onMatchUpdate();
      }
    } catch (error) {
      console.error('Failed to accept match:', error);
      alert('Nepodařilo se přijmout zápas');
      setLoading(false);
    }
  };

  const handleRejectMatch = async () => {
    if (!window.confirm('Opravdu chcete odmítnout tento zápas?')) {
      return;
    }

    setLoading(true);
    try {
      await api.post(`/matches/${match.id}/reject`);
      navigate('/');
    } catch (error) {
      console.error('Failed to reject match:', error);
      alert('Nepodařilo se odmítnout zápas');
    } finally {
      setLoading(false);
    }
  };

  // Pending match - waiting for acceptance
  if (match.status === 'pending') {
    const leftParticipant = match.participants.find((p: any) => p.side === 'left');
    const rightParticipant = match.participants.find((p: any) => p.side === 'right');

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="text-6xl sm:text-7xl mb-4">⏳</div>
            <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
              {isInvitedTeam ? 'Pozvánka k zápasu' : 'Čeká se na přijetí'}
            </h2>
            <p className="text-gray-600 text-lg">
              {isInvitedTeam
                ? 'Váš tým byl vyzván k soutěžnímu zápasu'
                : 'Čeká se, až druhý tým přijme výzvu'}
            </p>
          </div>

          <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card border border-white mb-8">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-3 rounded-2xl font-bold text-lg">
                Soutěžní zápas
              </div>
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Týmy
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
              <div className="bg-gradient-to-br from-green-100 to-green-200 p-6 rounded-2xl text-center">
                <div className="text-sm text-green-700 font-bold mb-2">HOSTITEL</div>
                <div className="text-5xl mb-3">👥</div>
                <div className="text-xl font-bold text-gray-900">
                  {leftParticipant?.team?.name || 'Tým 1'}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-6 rounded-2xl text-center">
                <div className="text-sm text-blue-700 font-bold mb-2">POZVANÝ</div>
                <div className="text-5xl mb-3">👥</div>
                <div className="text-xl font-bold text-gray-900">
                  {rightParticipant?.team?.name || 'Tým 2'}
                </div>
              </div>
            </div>

            {isInvitedTeam && (
              <div className="space-y-4">
                <button
                  onClick={handleAcceptMatch}
                  disabled={loading}
                  className="w-full py-5 font-bold text-white text-xl bg-gradient-to-r from-green-500 to-green-600 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50"
                >
                  {loading ? 'Přijímám...' : 'Přijmout výzvu ✓'}
                </button>
                <button
                  onClick={handleRejectMatch}
                  disabled={loading}
                  className="w-full py-4 font-bold text-gray-700 text-lg bg-gray-200 hover:bg-gray-300 rounded-2xl transition-all duration-200 disabled:opacity-50"
                >
                  Odmítnout ✗
                </button>
              </div>
            )}

            {isHostTeam && (
              <div className="text-center">
                <div className="inline-flex items-center bg-yellow-50 border border-yellow-200 rounded-xl px-6 py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600 mr-3"></div>
                  <span className="text-yellow-800 font-semibold">
                    Čeká se na odpověď druhého týmu...
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Started match - show original lobby (shouldn't normally happen, but kept for compatibility)
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <div className="text-6xl sm:text-7xl mb-4">🎮</div>
          <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
            Zápas přijat!
          </h2>
          <p className="text-gray-600 text-lg">Připravte se na hru!</p>
        </div>

        <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card border border-white mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-3 rounded-2xl font-bold text-lg">
              {match.mode === 'competitive' ? 'Soutěžní zápas' : 'Přátelský zápas'}
            </div>
          </div>

          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
            Hráči připraveni
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
            {match.participants.map((p: any, index: number) => (
              <div
                key={p.id}
                className="bg-gradient-to-br from-primary-100 to-secondary-100 p-6 rounded-2xl text-center transition-all duration-200"
              >
                <div className="text-5xl mb-3">👥</div>
                <div className="text-xl font-bold text-gray-900">
                  {p.team?.name || p.user?.name || `Hráč ${index + 1}`}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <div className="text-green-800 font-semibold text-lg mb-2">
              Zápas byl přijat!
            </div>
            <div className="text-green-600 text-sm">
              Hra se spustí automaticky...
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchLobby;
